import React, { useEffect } from "react";
import { useNavigate } from "react-router";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { ImageSourceSetData } from "../../../../common/classes/ImageSourceSet";
import { SonicEvent } from "../../../../common/classes/SonicEvent";
import Form from "../../../../common/forms/Form";
import FormInput from "../../../../common/forms/FormInput";
import TagInput from "../../../../common/forms/TagInput";
import LinkFieldInput from "../../../../common/forms/LinkFieldInput";
import BackBar from "../../../../common/nav/BackBar";
import SaveButton from "../../../../common/nav/SaveButton";
import NotificationModal from "../../../../common/settings-modal/NotificationModal";
import SelenaA from "../../../../assets/img/service/selena-a.webp";
import { ValType } from "../../../../utils/ValidationFunctions";
import { getCurrentDateForInputs } from "../../../../utils/GeneralUtils";
import { RequestState } from "../../../../utils/RequestState";
import { TermType } from "../../../../utils/useTerm";
import styles from "../EditBase.module.scss";
import LoadingSpinner from "../../../../common/LoadingSpinner";
import { SonicTermData } from "../../../../common/classes/SonicTerm";
import SelectMultipleTerms from "../../../../common/forms/SelectMultipleTerms";
import { genOptionsToSelectTime } from "./EventTimeFunc";
import FormMultipleImages from "../../../../common/forms/FormMultipleImages";
import SonicLink from "../../../../common/routing/SonicLink";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

type Props = {
  event?: SonicEvent;
  eventTypes: [string, string][] | [];
  imgItems: Array<File | ImageSourceSetData>;
  mediaItems: Array<{ name: string; url: string }>;
  relatedItems: Array<{ name: string; url: string }>;
  isHasChanges: boolean;
  error: boolean;
  isStudent: boolean;
  handleInputChange: (value: File | string | undefined, name: string) => void;
  handleSubmit?: () => void;
  handleSave?: () => void;
  deleteImgItem: (img: File | ImageSourceSetData, i: number) => void;
  handleMedia: (value: Array<{ name: string; url: string }>) => void;
  handleRelatedLinks: (value: Array<{ name: string; url: string }>) => void;
  requestState: RequestState;

  // MyGame
  games?: Array<SonicTermData>;
  handleGames: (value: Array<SonicTermData>) => void;
};
const EditEvent: React.FC<Props> = ({
  event,
  eventTypes,
  imgItems,
  mediaItems,
  relatedItems,
  isHasChanges,
  error,
  isStudent,
  handleInputChange,
  handleSubmit,
  handleSave,
  deleteImgItem,
  handleMedia,
  handleRelatedLinks,
  requestState,
  games,
  handleGames,
}) => {
  const navigate = useNavigate();

  const genNotificationColor = () => {
    switch (requestState) {
      case RequestState.Pending:
      case RequestState.Failed:
        return "#fe493d";
      case RequestState.Doing:
        return "#f79b00";
      case RequestState.Success:
        return "#27c900";
      default:
        return "";
    }
  };

  const genNotificationMessage = () => {
    switch (requestState) {
      case RequestState.Pending:
        return "保存していない変更があります";
      case RequestState.Doing:
        return "通信中です";
      case RequestState.Success:
        return "保存が完了しました";
      case RequestState.Failed:
        return "保存に失敗しました";
      default:
        return "";
    }
  };

  const genNotificationIcon = (): IconProp | undefined => {
    switch (requestState) {
      case RequestState.Pending:
      case RequestState.Failed:
        return ["fal", "exclamation-circle"];
      case RequestState.Success:
        return ["fal", "check-circle"];
      default:
        return undefined;
    }
  };

  useEffect(() => {
    if (requestState === RequestState.Doing) {
      window.scrollTo(0, 0);
    }
  }, [requestState]);

  return (
    <>
      {requestState !== RequestState.Doing ? (
        <>
          <BackBar
            title={event ? "イベント編集" : "イベント作成"}
            editable={true}
            handleClose={() => navigate(-1)}
            submitHandler={handleSubmit}
            saveHandler={handleSave}
            hideSubmitButton={
              (!event && !isHasChanges) || (event && isHasChanges)
                ? true
                : false
            }
            hideSaveButton={!isHasChanges ? true : false}
          />
          <Form handler={() => {}} className={styles["p-edit__wrapper"]}>
            <div className={styles["p-edit__form"]}>
              <FormInput
                type="text"
                label="イベント名"
                label_class={`${styles["p-edit__form-title"]} ${styles["required-mark"]}`}
                name="sonic_name"
                style_class={styles["p-edit__form-input"]}
                handler={handleInputChange}
                val_type={ValType.NotNull}
                initial_value={event?.name}
              />
            </div>
            <div className={styles["p-edit__form"]}>
              <FormInput
                type="select"
                label="イベントカテゴリ"
                name="sonic_event_type"
                holder_class={styles["holder"]}
                select_class={styles["select"]}
                label_class={`${styles["p-edit__form-title"]} ${styles["required-mark"]}`}
                style_class={styles["p-edit__form-input"]}
                handler={handleInputChange}
                val_type={ValType.NotNull}
                initial_value={event?.event_type?.id}
                options={eventTypes}
                placeholder="カテゴリを選択"
                merit_text="カテゴリ検索でヒットします。"
              />
            </div>
            <div className={styles["p-edit__form"]}>
              <div className={styles["p-edit__form-title"]}>開催日時</div>
              <p>イベントが行われる日時を設定できます。</p>
              <div className={styles["p-edit__form-date-and-time"]}>
                <div className={styles["p-edit__form-date-and-time__box"]}>
                  <p className={styles["required-mark"]}>開始日</p>
                  <FormInput
                    type="date"
                    label=""
                    name="sonic_start_date"
                    val_type={ValType.NotNull}
                    handler={handleInputChange}
                    style_class={styles["p-edit__form-input"]}
                    input_wrap_class={styles["date-wrap"]}
                    initial_value={event?.start_date}
                    min_date={getCurrentDateForInputs()}
                  />
                </div>
                <div className={styles["p-edit__form-date-and-time__box"]}>
                  <p>開始時刻</p>
                  <FormInput
                    type="select"
                    label=""
                    name="sonic_start_time"
                    handler={handleInputChange}
                    style_class={styles["p-edit__form-input"]}
                    holder_class={styles["holder"]}
                    select_class={styles["select"]}
                    initial_value={event ? event.start_time : 0}
                    options={genOptionsToSelectTime()}
                    placeholder="時刻を選択"
                  />
                </div>

                <div className={styles["p-edit__form-date-and-time__box"]}>
                  <p className={styles["required-mark"]}>終了日</p>
                  <FormInput
                    type="date"
                    label=""
                    name="sonic_end_date"
                    val_type={ValType.NotNull}
                    handler={handleInputChange}
                    style_class={styles["p-edit__form-input"]}
                    input_wrap_class={styles["date-wrap"]}
                    initial_value={event?.end_date}
                    min_date={getCurrentDateForInputs()}
                  />
                </div>
                <div className={styles["p-edit__form-date-and-time__box"]}>
                  <p>終了時刻</p>
                  <FormInput
                    type="select"
                    label=""
                    name="sonic_end_time"
                    handler={handleInputChange}
                    style_class={styles["p-edit__form-input"]}
                    holder_class={styles["holder"]}
                    select_class={styles["select"]}
                    initial_value={event?.end_time}
                    options={genOptionsToSelectTime()}
                    placeholder="時刻を選択"
                  />
                </div>
              </div>
            </div>
            <div className={styles["p-edit__form"]}>
              <FormInput
                type="radio"
                label="開催地"
                name="sonic_location"
                label_class={`${styles["p-edit__form-title"]} ${styles["required-mark"]}`}
                handler={handleInputChange}
                val_type={ValType.NotNull}
                initial_value={event?.location}
                options={[
                  ["offline", "オフライン"],
                  ["online", "オンライン"],
                ]}
                merit_text="イベント詳細画面で表示されます。"
              />
            </div>
            <div className={styles["p-edit__form"]}>
              <FormInput
                type="textarea"
                label="イベント概要"
                label_class={`${styles["p-edit__form-title"]} ${styles["required-mark"]}`}
                name="sonic_overview"
                style_class={styles["p-edit__form-input"]}
                handler={handleInputChange}
                initial_value={event?.overview}
                val_type={ValType.NotNull}
                rows={16}
                merit_text="あなたのイベントをアピールしましょう。"
              />
            </div>
            <div className={styles["p-edit__form"]}>
              <div className={styles["p-edit__form-title"]}>ゲーム</div>
              <p>ゲームから検索されやすくなります</p>
              <div className={styles["p-edit__form__current-mygame"]}>
                {games && games.length > 0 ? (
                  <div className={styles["p-edit__form__current-mygame-list"]}>
                    <p>設定済み</p>
                    <ul>
                      {games.map((game, i) => (
                        <li
                          key={i}
                          className={
                            styles["p-edit__form__current-mygame-list-item"]
                          }
                        >
                          {game.name}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <div
                    className={
                      styles["p-edit__form__current-mygame-list--none"]
                    }
                  >
                    {"まだゲームが設定されていないようです。"}
                  </div>
                )}
              </div>
              <SelectMultipleTerms
                termType={TermType.Games}
                termName="ゲーム"
                buttonText="ゲームを選ぶ"
                terms={games ? games : []}
                handleTerms={handleGames}
              />
              <div className={styles["p-edit__help"]}>
                <SonicLink to="/contact" newTab={true}>
                  {"ご希望のゲームがない方はこちら"}
                </SonicLink>
              </div>
            </div>
            <div className={styles["p-edit__form"]}>
              <div className={styles["p-edit__form-title"]}>
                画像
                <div className={styles["p-edit__title-desc"]}>
                  画像を設定することであなたのイベントがユーザーの目に留まりやすくなります。
                </div>
              </div>
              <FormMultipleImages
                imgItems={imgItems}
                deleteHandler={deleteImgItem}
                addHandler={handleInputChange}
                addBtnClass={styles["p-edit__form-addImg"]}
                supplement={
                  <div className={styles["p-edit__form-supplement"]}>
                    <p className={styles["p-edit__form-supplement__text"]}>
                      ※１枚目の画像がサムネイルになります
                    </p>
                    <p className={styles["p-edit__form-supplement__limit"]}>
                      アップロード上限：最大5枚、トータル10MB以内
                    </p>
                  </div>
                }
              />
            </div>
            <div className={styles["p-edit__form"]}>
              <TagInput
                label="タグ"
                label_class={styles["p-edit__form-title"]}
                name="sonic_tags"
                id="tags"
                input_class={styles["p-edit__form-input"]}
                tag_class={styles["p-edit__form-input-tag"]}
                handler={handleInputChange}
                value={event?.tags}
                merit_text={
                  "タグで絞り込みが可能になります。\n例：配信あり、オフライン限定、無料イベント など"
                }
              />
              <div className={styles["p-edit__form-supplement"]}>
                <p className={styles["p-edit__form-supplement-text"]}>
                  {`各タグの入力後は必ずEnterキーを押してください。\nスマートフォンをご利用の方はキーボード右下の完了ボタンを押してください。\n`}
                  確定後は
                  <span
                    className={`${styles["p-edit__form-input-tag"]} ${styles["preview"]}`}
                  >
                    {`#タグ名`}
                    <span>
                      <FontAwesomeIcon icon={["fas", "times"]} />
                    </span>
                  </span>
                  の表示になります。
                  {`\n※Spaceキーは使用できません`}
                </p>
              </div>
            </div>
            <div className={styles["p-edit__form"]}>
              <div className={styles["p-edit__form-title"]}>
                配信情報
                <div className={styles["p-edit__title-desc"]}>
                  イベント詳細にリンクを設置できます。
                </div>
              </div>
              <p className={styles["p-edit__form-supplement__text"]}>
                タイトルとURLを入力後、「+」ボタンを押して確定できます。
              </p>
              <LinkFieldInput
                id="mediaField"
                name="sonic_media"
                handler={handleMedia}
                value={mediaItems}
                style_class={`${styles["p-edit__form-input-repeater"]} ${styles["mb-15"]}`}
                label_class={styles["p-edit__form-title"]}
                add_button_class={styles["p-edit__form-add"]}
                list_class={styles["p-edit__form-list"]}
              />
            </div>
            <div className={styles["p-edit__form"]}>
              <div className={styles["p-edit__form-title"]}>アクセス</div>
              <FormInput
                type="text"
                label="住所"
                label_class={styles["p-edit__form-title"]}
                name="sonic_address"
                style_class={`${styles["p-edit__form-input"]} ${styles["mb-15"]}`}
                handler={handleInputChange}
                initial_value={event?.address}
                merit_text="イベント詳細に表示されます。"
              />
              <FormInput
                type="textarea"
                label="アクセス方法"
                label_class={styles["p-edit__form-title"]}
                name="sonic_direction"
                style_class={`${styles["p-edit__form-input"]} ${styles["mb-15"]}`}
                handler={handleInputChange}
                initial_value={event?.direction}
                rows={4}
                merit_text="イベント詳細に表示されます。"
              />
              <FormInput
                type="text"
                label="GoogleMap"
                label_class={styles["p-edit__form-title"]}
                name="sonic_map"
                style_class={styles["p-edit__form-input"]}
                handler={handleInputChange}
                initial_value={event?.map}
                merit_text="イベント詳細にリンクが表示されます。"
              />
            </div>
            <div className={styles["p-edit__form"]}>
              <div className={styles["p-edit__form-title"]}>
                関連リンク
                <div className={styles["p-edit__title-desc"]}>
                  イベント詳細に公式サイトなどのリンクを設置できます。
                </div>
              </div>
              <p className={styles["p-edit__form-supplement__text"]}>
                タイトルとURLを入力後、「+」ボタンを押して確定できます。
              </p>
              <LinkFieldInput
                id="linkField"
                name="sonic_related_links"
                handler={handleRelatedLinks}
                value={relatedItems}
                style_class={`${styles["p-edit__form-input-repeater"]} ${styles["mb-15"]}`}
                label_class={styles["p-edit__form-title"]}
                add_button_class={styles["p-edit__form-add"]}
                list_class={styles["p-edit__form-list"]}
              />
            </div>
            {isStudent ? (
              <div className={styles["p-edit__form"]}>
                <FormInput
                  type="radio"
                  label="表示設定"
                  name="sonic_display_setting"
                  handler={handleInputChange}
                  options={[
                    ["general", "一般"],
                    ["student", "学生限定"],
                  ]}
                  label_class={styles["p-edit__form-title"]}
                  initial_value={
                    event?.role_restriction.includes("jikei_student")
                      ? "student"
                      : "general"
                  }
                  merit_text="「学生限定」にすると学生のみに表示されます。"
                />
              </div>
            ) : null}
          </Form>
          {error ? (
            <div className={styles["p-edit__error"]}>
              必須項目が未入力、または入力内容に誤りがあります
            </div>
          ) : null}
          {handleSave ? <SaveButton title="保存" onClick={handleSave} /> : null}
          {(handleSubmit && !isHasChanges) || !handleSave ? (
            <SaveButton title="公開申請" onClick={handleSubmit} />
          ) : null}
        </>
      ) : (
        <LoadingSpinner space={1} />
      )}
      {isHasChanges || requestState !== RequestState.Waiting ? (
        <NotificationModal
          color={genNotificationColor()}
          icon_class={genNotificationIcon()}
          message={genNotificationMessage()}
          img_src={requestState === RequestState.Success ? SelenaA : undefined}
        />
      ) : null}
    </>
  );
};

export default EditEvent;
